/**
 * rx-animations.js — Relaxer Store Animation Controller
 * Brand: Relaxer orthopedic pillows | #138AB1 teal system
 * Pairs with: rx-animations.css
 */
(function () {
  'use strict';

  /* ============================================================
     UTILITIES
  ============================================================ */

  function qs(selector, ctx) {
    try {
      return (ctx || document).querySelector(selector);
    } catch (e) {
      return null;
    }
  }

  function qsa(selector, ctx) {
    try {
      return Array.from((ctx || document).querySelectorAll(selector));
    } catch (e) {
      return [];
    }
  }

  var prefersReducedMotion = (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  /* ============================================================
     1. PAGE LOADER CONTROLLER
  ============================================================ */
  (function initPageLoader() {
    var MIN_MS = 800;
    var SAFETY_MS = 3500;
    var startTime = Date.now();
    var dismissed = false;
    var safetyTimer = null;

    // If reduced motion: skip loader entirely
    if (prefersReducedMotion) {
      document.body.classList.add('rx-loader-done');
      return;
    }

    // Only show the loader ONCE per browser session.
    // On every subsequent page navigation within the same tab session,
    // skip instantly — prevents double-flash when navigating between pages.
    try {
      if (sessionStorage.getItem('rx-loader-shown')) {
        document.body.classList.add('rx-loader-done');
        return;
      }
      sessionStorage.setItem('rx-loader-shown', '1');
    } catch (e) {
      // sessionStorage unavailable (private mode edge case) — show loader normally
    }

    var taglineEl = document.querySelector('.rx-loader-tagline');
    var messages = ['Comfort. Engineered.', 'Loading your comfort…', 'Almost ready…'];
    var msgIdx = 0;
    var msgTimer = setInterval(function () {
      if (dismissed || !taglineEl) { clearInterval(msgTimer); return; }
      msgIdx = (msgIdx + 1) % messages.length;
      taglineEl.style.transition = 'opacity 0.2s ease';
      taglineEl.style.opacity = '0';
      setTimeout(function () {
        if (taglineEl && !dismissed) {
          taglineEl.textContent = messages[msgIdx];
          taglineEl.style.opacity = '1';
        }
      }, 230);
    }, 1400);

    function dismissLoader() {
      if (dismissed) return;
      dismissed = true;
      clearInterval(msgTimer);
      if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
      if (taglineEl) {
        taglineEl.style.transition = '';
        taglineEl.textContent = 'Comfort. Engineered.';
        taglineEl.style.opacity = '1';
      }
      // Push bar fill to 100% instantly on dismiss
      var barFill = document.querySelector('.rx-loader-bar-fill');
      if (barFill) { barFill.style.width = '100%'; barFill.style.transition = 'width 0.3s ease'; }
      document.body.classList.add('rx-loader-done');
    }

    function tryDismiss() {
      var elapsed = Date.now() - startTime;
      var remaining = MIN_MS - elapsed;
      if (remaining <= 0) {
        dismissLoader();
      } else {
        setTimeout(dismissLoader, remaining);
      }
    }

    // Safety net — always dismiss within SAFETY_MS no matter what
    safetyTimer = setTimeout(dismissLoader, SAFETY_MS);

    // Wait for window.load — all assets (images, videos) fully loaded.
    // Loader stays visible until the page is actually ready to use.
    // DOMContentLoaded fires once HTML is parsed -- page is usable without waiting for images.
    if (document.readyState !== 'loading') {
      tryDismiss();
    } else {
      document.addEventListener('DOMContentLoaded', function onDCL() {
        document.removeEventListener('DOMContentLoaded', onDCL);
        tryDismiss();
      });
    }
    // bfcache: back/forward restores instantly -- ensure loader is dismissed.
    window.addEventListener('pageshow', function(e) {
      if (e.persisted) { document.body.classList.add('rx-loader-done'); }
    });
  }());

  /* ============================================================
     2. INTERSECTION OBSERVER — [data-rx-anim]
  ============================================================ */
  var rxObserver = null;

  function initScrollAnimations() {
    var elements = qsa('[data-rx-anim]');
    if (!elements.length) return;

    // Graceful fallback if IntersectionObserver not supported
    if (typeof IntersectionObserver === 'undefined') {
      elements.forEach(function (el) {
        try {
          el.classList.add('rx-anim-in');
        } catch (e) {}
      });
      return;
    }

    rxObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;

        try {
          // Read stagger delay from data-rx-delay-N attribute (1–8)
          var delayAttr = null;
          for (var n = 1; n <= 8; n++) {
            if (el.hasAttribute('data-rx-delay-' + n)) {
              delayAttr = n;
              break;
            }
          }
          if (delayAttr !== null) {
            el.style.transitionDelay = (delayAttr * 0.1) + 's';
          }

          el.classList.add('rx-anim-in');
          // Also add rx-visible for elements that check that class (marquee, usp, etc.)
          el.classList.add('rx-visible');

          // After animation ends: remove will-change, unobserve
          function onTransitionEnd(evt) {
            try {
              // Only fire on the primary property to avoid multiple firings
              var prop = evt && evt.propertyName;
              if (prop && prop !== 'opacity' && prop !== 'clip-path' && prop !== 'transform') return;
              el.style.willChange = 'auto';
              el.classList.add('rx-anim-done');
              el.removeEventListener('transitionend', onTransitionEnd);
              if (rxObserver) rxObserver.unobserve(el);
            } catch (e) {}
          }
          el.addEventListener('transitionend', onTransitionEnd);
        } catch (e) {}
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });

    elements.forEach(function (el) {
      try {
        rxObserver.observe(el);
      } catch (e) {}
    });
  }

  /* ============================================================
     3. HEADING WORD SPLIT — [data-rx="top"]
  ============================================================ */
  function initWordSplit() {
    var headings = qsa('[data-rx="top"]');
    headings.forEach(function (el) {
      try {
        // Skip if already processed
        if (el.getAttribute('data-rx-split-done') === '1') return;

        var rawText = el.textContent || '';
        var words = rawText.trim().split(/\s+/).filter(function (w) { return w.length > 0; });
        if (!words.length) return;

        el.innerHTML = '';
        words.forEach(function (word, i) {
          var span = document.createElement('span');
          span.className = 'rx-word';
          span.textContent = word + (i < words.length - 1 ? ' ' : '');
          span.setAttribute('data-rx-anim', 'fade-up');
          // Cap delay attribute at 8
          var delayIndex = Math.min(i + 1, 8);
          span.setAttribute('data-rx-delay-' + delayIndex, '');
          el.appendChild(span);
        });

        el.setAttribute('data-rx-split-done', '1');
        // Add to observer — the heading itself carries [data-rx="top"]
        // and the CSS handles .rx-anim-in .rx-word, so observe the heading
        if (rxObserver) {
          rxObserver.observe(el);
        }
      } catch (e) {}
    });
  }

  /* ============================================================
     4. PRODUCT CARD HOVER ENHANCEMENTS
  ============================================================ */
  function initCardHovers() {
    var cards = qsa('.card-wrapper');
    cards.forEach(function (card) {
      try {
        card.addEventListener('mouseenter', function () {
          try { card.classList.add('rx-card-hover'); } catch (e) {}
        });
        card.addEventListener('mouseleave', function () {
          try { card.classList.remove('rx-card-hover'); } catch (e) {}
        });
      } catch (e) {}
    });
  }

  /* ============================================================
     5. SCROLL PROGRESS INDICATOR
     NOTE: The #rx-scroll-progress HTML element and its scroll
     handler live in theme.liquid (script block) and the CSS rule
     lives in rx-animations.css. This function is intentionally
     a no-op to avoid creating a duplicate element.
  ============================================================ */
  function initScrollProgress() {
    // Handled by the dedicated HTML <div id="rx-scroll-progress">
    // and the scroll listener in theme.liquid. No action needed here.
  }

  /* ============================================================
     6. SMOOTH SECTION TRANSITIONS — active section detection
  ============================================================ */
  function initSectionTracking() {
    if (typeof IntersectionObserver === 'undefined') return;

    try {
      var sections = qsa('section, [data-section-type], .shopify-section');
      if (!sections.length) return;

      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          try {
            if (entry.isIntersecting) {
              // Remove active from all, add to this one
              sections.forEach(function (s) {
                s.classList.remove('rx-section-active');
              });
              entry.target.classList.add('rx-section-active');
            }
          } catch (e) {}
        });
      }, {
        threshold: 0.4
      });

      sections.forEach(function (section) {
        try {
          sectionObserver.observe(section);
        } catch (e) {}
      });
    } catch (e) {}
  }

  /* ============================================================
     7. LAZY IMAGE ENHANCEMENT
  ============================================================ */
  function initLazyImages() {
    try {
      var viewportBottom = window.scrollY + window.innerHeight;
      var imgs = qsa('img');

      imgs.forEach(function (img) {
        try {
          // Only add lazy to images that are below the fold and don't already have it
          if (img.getAttribute('loading') === 'lazy') return;

          var rect = img.getBoundingClientRect();
          var imgTop = rect.top + window.scrollY;

          if (imgTop > viewportBottom) {
            img.setAttribute('loading', 'lazy');
            img.setAttribute('decoding', 'async');
          }

          // Fade-in on load for all images that haven't loaded yet
          if (!img.complete) {
            img.style.opacity = '0';
            img.style.transition = 'opacity 0.45s ease';
            img.addEventListener('load', function () {
              try {
                img.style.opacity = '1';
              } catch (e) {}
            });
            img.addEventListener('error', function () {
              try {
                img.style.opacity = '1';
              } catch (e) {}
            });
          }
        } catch (e) {}
      });
    } catch (e) {}
  }

  /* ============================================================
     8. PARALLAX — hero slide images (desktop only, passive)
  ============================================================ */
  function initParallax() {
    if (prefersReducedMotion) return;
    // Skip parallax on mobile devices entirely (430px and below)
    if (window.innerWidth <= 430) return;

    try {
      // Target both our custom banner slides and Dawn hero slides
      var heroImgs = qsa(
        '.rx-banner-slide picture, .rx-banner-slide img,' +
        '.rx-hero-slide img, .rx-hero-slide picture, .rx-hero-slide .banner__media'
      );
      if (!heroImgs.length) return;

      // Get hero section height to limit parallax to in-view range only
      var heroSection = qs('#rx-banner-wrap') || qs('.rx-hero-slide') || null;

      var rafPending = false;

      function applyParallax() {
        rafPending = false;
        // Re-check viewport width on each tick — handles orientation change
        if (window.innerWidth <= 430) return;

        try {
          var scrollY = window.pageYOffset || 0;
          // Only apply parallax while hero is in view to avoid artifacts
          if (heroSection) {
            var heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
            if (scrollY > heroBottom) return;
          }

          heroImgs.forEach(function (img) {
            try {
              // Subtle 0.15x parallax — cinematic but not nauseating
              img.style.transform = 'translateY(' + (scrollY * 0.15) + 'px)';
            } catch (e) {}
          });
        } catch (e) {}
      }

      window.addEventListener('scroll', function () {
        if (!rafPending) {
          rafPending = true;
          requestAnimationFrame(applyParallax);
        }
      }, { passive: true });
    } catch (e) {}
  }

  /* ============================================================
     9. TESTIMONIAL MARQUEE OBSERVER
     The marquee wrap uses data-rx-anim="slide-reveal" which is
     handled by initScrollAnimations. This function adds a dedicated
     fallback observer for .rx-testi-marquee-wrap in case the element
     is not picked up by the generic [data-rx-anim] observer (e.g. if
     the attribute was added after DOMContentLoaded).
  ============================================================ */
  function initMarqueeReveal() {
    if (prefersReducedMotion) return;

    try {
      var marquee = qs('.rx-testi-marquee-wrap');
      if (!marquee) return;

      function revealMarquee() {
        try {
          if (marquee.classList.contains('rx-anim-in')) return;
          var rect = marquee.getBoundingClientRect();
          // Fire when top of marquee is within 80% of viewport height
          if (rect.top < window.innerHeight * 0.85) {
            marquee.classList.add('rx-anim-in');
            marquee.classList.add('rx-visible');
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('touchmove', onScroll);
          }
        } catch (e) {}
      }

      var scrollRafPending = false;
      function onScroll() {
        if (!scrollRafPending) {
          scrollRafPending = true;
          requestAnimationFrame(function () {
            scrollRafPending = false;
            revealMarquee();
          });
        }
      }

      // Check immediately in case it's already in view
      revealMarquee();

      // Also set up IntersectionObserver as primary method
      if (typeof IntersectionObserver !== 'undefined') {
        var marqueeObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            try {
              marquee.classList.add('rx-anim-in');
              marquee.classList.add('rx-visible');
              marqueeObserver.unobserve(entry.target);
              window.removeEventListener('scroll', onScroll);
              window.removeEventListener('touchmove', onScroll);
            } catch (e) {}
          });
        }, {
          threshold: 0.05,
          rootMargin: '0px 0px 0px 0px'
        });
        marqueeObserver.observe(marquee);
      }

      // Belt-and-suspenders: scroll listener fallback
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('touchmove', onScroll, { passive: true });
    } catch (e) {}
  }

  /* ============================================================
     10. CART DRAWER ANIMATION
  ============================================================ */
  function initCartDrawer() {
    try {
      // Watch for cart drawer opening via MutationObserver on class/attr changes
      // Support both custom rx-cart-drawer ID and Dawn's default CartDrawer ID
      var drawer = qs('#rx-cart-drawer') || qs('#CartDrawer') || qs('[data-cart-drawer]') || qs('.cart-drawer');
      if (!drawer) {
        // Try to find it lazily — it may be injected later
        var bodyObserver = new MutationObserver(function (mutations) {
          try {
            var found = qs('#rx-cart-drawer') || qs('#CartDrawer') || qs('[data-cart-drawer]') || qs('.cart-drawer');
            if (found) {
              bodyObserver.disconnect();
              attachCartObserver(found);
            }
          } catch (e) {}
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });
        return;
      }
      attachCartDrawer(drawer);
    } catch (e) {}
  }

  function attachCartDrawer(drawer) {
    try {
      var attrObserver = new MutationObserver(function () {
        try {
          var isOpen = (
            drawer.classList.contains('is-open') ||
            drawer.classList.contains('active') ||
            drawer.classList.contains('is-active') ||
            drawer.getAttribute('aria-hidden') === 'false' ||
            drawer.hasAttribute('open')
          );

          if (isOpen) {
            drawer.classList.add('rx-cart-open');
            animateCartItems(drawer);
          } else {
            drawer.classList.remove('rx-cart-open');
            resetCartItems(drawer);
          }
        } catch (e) {}
      });

      attrObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden', 'open']
      });
    } catch (e) {}
  }

  // Keep the original name in case it was used as a recursive reference
  var attachCartObserver = attachCartDrawer;

  function animateCartItems(drawer) {
    try {
      var items = qsa('.cart-item, [data-cart-item], .rx-cart-item', drawer);
      items.forEach(function (item, i) {
        try {
          item.style.opacity = '0';
          item.style.transform = 'translateX(-24px)';
          item.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          item.style.transitionDelay = (i * 0.07) + 's';

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              try {
                item.style.opacity = '1';
                item.style.transform = 'translateX(0)';
              } catch (e) {}
            });
          });
        } catch (e) {}
      });
    } catch (e) {}
  }

  function resetCartItems(drawer) {
    try {
      var items = qsa('.cart-item, [data-cart-item], .rx-cart-item', drawer);
      items.forEach(function (item) {
        try {
          item.style.opacity = '';
          item.style.transform = '';
          item.style.transition = '';
          item.style.transitionDelay = '';
        } catch (e) {}
      });
    } catch (e) {}
  }


  /* ============================================================
     FAQ SECTION OBSERVER — .rx-faq-item
     These don't have data-rx-anim attributes so need their own observer.
  ============================================================ */
  function initFaqObserver() {
    var faqItems = qsa('.rx-faq-item');
    if (!faqItems.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      faqItems.forEach(function (el) { el.classList.add('rx-anim-in', 'rx-anim-done'); });
      return;
    }

    var faqObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('rx-anim-in');
        setTimeout(function () {
          el.classList.add('rx-anim-done');
          el.style.willChange = 'auto';
        }, 600);
        faqObserver.unobserve(el);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });

    faqItems.forEach(function (el) { faqObserver.observe(el); });
  }

  /* ============================================================
     INIT SEQUENCE
  ============================================================ */
  function init() {
    // 3 — Word split must happen before observer setup
    initWordSplit();

    // 2 — Set up intersection observer for [data-rx-anim]
    initScrollAnimations();

    // 4
    initCardHovers();

    // 5
    initScrollProgress();

    // 6
    initSectionTracking();

    // 7
    initLazyImages();

    // 8
    initParallax();

    // 9 — Marquee fallback observer (runs after generic observer so it can check)
    setTimeout(initMarqueeReveal, 50);

    // 10
    initCartDrawer();

    // FAQ
    initFaqObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
